/**
 * Dashboard API service - All dashboard data fetching centralized
 */
import api from './api';

const safe = (promise, fallback) =>
  promise.catch(() => ({ data: fallback }));

export const dashboardApi = {
  /**
   * Fetch all dashboard data in parallel
   */
  async fetchAll() {
    const [
      vehiclesRes,
      publicationsRes,
      settingsRes,
      appointmentsRes,
      offersRes,
      listingsRes,
      suggestionsRes,
      oauthRes,
      savedSearchesRes,
    ] = await Promise.all([
      safe(api.get('/vehicles'), { vehicles: [] }),
      safe(api.get('/publications'), { publications: [] }),
      safe(api.get('/settings'), {}),
      safe(api.get('/marketplace/appointments/received'), { appointments: [] }),
      safe(api.get('/marketplace/offers/received'), { offers: [] }),
      safe(api.get('/marketplace/my-listings'), { listings: [] }),
      safe(api.get('/marketplace/suggestions'), { suggestions: [] }),
      safe(api.get('/oauth/accounts'), { accounts: [] }),
      safe(api.get('/marketplace/searches/saved'), { searches: [] }),
    ]);

    const listings = listingsRes.data.listings || listingsRes.data || [];

    return {
      vehicles: vehiclesRes.data.vehicles || [],
      publications: publicationsRes.data.publications || [],
      settings: settingsRes.data || {},
      appointments: appointmentsRes.data.appointments || [],
      offers: offersRes.data.offers || [],
      listings: Array.isArray(listings) ? listings : [],
      suggestions: suggestionsRes.data.suggestions || [],
      oauthAccounts: oauthRes.data.accounts || [],
      savedSearches: savedSearchesRes.data.searches || [],
    };
  },

  /** Accept offer inline */
  acceptOffer: (offerId) =>
    api.post(`/marketplace/offers/${offerId}/accept`),

  /** Reject offer inline */
  rejectOffer: (offerId) =>
    api.post(`/marketplace/offers/${offerId}/reject`),

  /** Counter offer */
  counterOffer: (offerId, data) =>
    api.post(`/marketplace/offers/${offerId}/counter`, data),

  /** Confirm appointment inline */
  confirmAppointment: (appointmentId) =>
    api.post(`/marketplace/appointments/${appointmentId}/confirm`),

  /** Reject appointment inline */
  rejectAppointment: (appointmentId, data) =>
    api.post(`/marketplace/appointments/${appointmentId}/reject`, data),

  /** Toggle SMS reminder for an appointment */
  toggleSmsReminder: (appointmentId, enabled) =>
    api.post(`/marketplace/appointments/${appointmentId}/sms-reminder`, { enabled }),

  /** Toggle call reminder for an appointment */
  toggleCallReminder: (appointmentId, enabled) =>
    api.post(`/marketplace/appointments/${appointmentId}/call-reminder`, { enabled }),

  /** Submit post-visit feedback (seller only) */
  submitAppointmentFeedback: (appointmentId, outcome, reason) =>
    api.post(`/marketplace/appointments/${appointmentId}/feedback`, { outcome, reason }),

  /** Cancel a scheduled publication */
  cancelPublication: (publicationId) =>
    api.delete(`/publications/${publicationId}`),

  /** Reschedule a scheduled publication */
  reschedulePublication: (publicationId, scheduledAt) =>
    api.patch(`/publications/${publicationId}/reschedule`, { scheduled_at: scheduledAt }),

  /** Get social media stats (insights from Instagram/Facebook) */
  getSocialStats: (days = 30) =>
    api.get(`/publications/stats?days=${days}`).then(r => r.data).catch(() => null),

  /** Get vitrine widget analytics */
  getVitrineAnalytics: (days = 30) =>
    api.get(`/settings/analytics/vitrine?days=${days}`).then(r => r.data).catch(() => null),

  /** Get social media stats (insights from Instagram/Facebook) */
  getSocialStats: (days = 30) =>
    api.get(`/publications/stats?days=${days}`).then(r => r.data).catch(() => null),

  /** Fetch sidebar badge counts */
  getBadgeCounts: () =>
    api.get('/dashboard/badges').then(r => r.data).catch(() => ({ stock: 0, social: 0, marketplace: 0 })),

  /** Get visibility card data (scores, platforms, recommendations) */
  getVisibility: () =>
    api.get('/dashboard/visibility').then(r => r.data).catch(() => null),

  /** Get stock insights (sell duration, etc.) */
  getInsights: () =>
    api.get('/vehicles/insights').then(r => r.data).catch(() => null),
};
